/* ============================================================
   TRADIE — Keep size selection working through re-renders/reposition
   Confirmed by diffing this draft against the LIVE published theme:
   assets/pf-custom.js, assets/pf-variant-picker.js and
   snippets/pf-color-mixer.liquid are otherwise identical — so this
   isn't a live-site bug, it's specific to something this draft does.

   REAL ROOT CAUSE (found after live actually worked with "pick a
   size, then Mixed colours" and draft didn't):
   size-selector-reposition.js moves <pf-size-selector> in the DOM
   (insertBefore) to sit next to the colour swatches. Per the DOM
   spec, moving an already-connected custom element via
   insertBefore/appendChild fires disconnectedCallback then
   connectedCallback on it — even though it's never actually removed
   from the page. PfSizeSelector (assets/pf-custom.js) keeps its
   AbortController in a class field created once at construction:

     #controller = new AbortController();
     connectedCallback() {
       const { signal } = this.#controller;
       this.sizeInputs.forEach(i => i.addEventListener("change", ..., { signal }));
     }
     disconnectedCallback() { this.#controller.abort(); }

   disconnectedCallback() aborts that controller; connectedCallback()
   then re-registers the 'change' listeners using the SAME (already
   permanently aborted) signal, since the field is never recreated.
   Registering a listener with an already-aborted signal silently
   does nothing. Net effect: after the one-time reposition move
   (which runs on DOMContentLoaded, so it's already happened well
   before a human can click anything), size radios still visually
   show selected (the black fill is plain CSS :checked styling,
   untouched by any of this), but the *real* 'change' handler that
   sets data-selected="true" and dispatches 'sizeselect' never fires
   again for the rest of the page's life — not just once, but for
   every single size click from then on.

   UPDATE: the first version of this fix only dispatched the
   compensating 'sizeselect' event when data-selected wasn't already
   "true" — meant to avoid double-firing in the split second before
   the reposition happens. But since data-selected flips to "true"
   on the very first click and then never resets, that guard silently
   blocked every size switch AFTER the first one: switching size a
   second or third time stopped re-dispatching 'sizeselect' at all,
   so PfColorMixer.renderColorPicker() (assets/pf-custom.js) never
   re-ran — the Mixed colours swatch picker and bundle tiles stayed
   frozen on stock data from whichever size was selected first,
   letting you add colourways that were actually out of stock for
   the size you'd since switched to. In practice the reposition has
   always already happened by the time a human can click a size
   radio, so the internal handler is dead before any real user
   interaction anyway — the guard wasn't preventing a real double-fire,
   just blocking every switch after the first. Removed it: this
   listener now dispatches 'sizeselect' on every genuine size change,
   matching how the live site's (still-working) internal handler
   behaves — always re-firing, which is exactly what
   PfColorMixer.renderColorPicker relies on to re-filter available
   colours and reset/gray-out selections based on the newly selected
   size's actual stock.

   Also still restores the last-picked size after a colour switch
   (see comment block further down) — that part of the analysis was
   correct too, both issues were compounding.
   ============================================================ */

(function () {
  var pfLastSize = null;

  document.addEventListener('change', function (e) {
    if (!e.target || !e.target.classList || !e.target.classList.contains('pf-size-input')) return;

    pfLastSize = e.target.getAttribute('data-size');

    var sizeSelector = e.target.closest('pf-size-selector');
    if (!sizeSelector) return;

    sizeSelector.dataset.selected = 'true';
    sizeSelector.dispatchEvent(new CustomEvent('sizeselect', {
      detail: {
        name: e.target.dataset.size,
        variant_id: e.target.value,
        quantity: e.target.dataset.quantity
      },
      bubbles: true,
      composed: true
    }));
  });

  // ---- Restore the previously-picked size after a colour switch ----
  // Switching colour (assets/pf-variant-picker.js, onButtonClick)
  // replaces the whole product info section's innerHTML with freshly
  // fetched server markup, rebuilding <pf-size-selector
  // data-selected="false"> from scratch and wiping out whatever size
  // was picked. The theme ships a mechanism for restoring it
  // (window.pfSizeStreak) but it targets #pf-size-variants markup
  // that doesn't exist in this theme's actual size selector, so it
  // never fires — confirmed this is the same on the live theme too,
  // not something introduced here.
  function restoreSizeSelection() {
    var sizeSelector = document.querySelector('pf-size-selector');
    if (!sizeSelector || !pfLastSize) return;
    if (sizeSelector.dataset.selected === 'true') return;

    var input = sizeSelector.querySelector(
      '.pf-size-input[data-size="' + pfLastSize.replace(/"/g, '\\"') + '"]'
    );
    if (!input || input.checked) return;

    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  document.addEventListener('DOMContentLoaded', restoreSizeSelection);

  var scheduled = false;
  var observer = new MutationObserver(function () {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(function () {
      scheduled = false;
      restoreSizeSelection();
    });
  });

  document.addEventListener('DOMContentLoaded', function () {
    observer.observe(document.body, { childList: true, subtree: true });
  });
})();
